import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { tokenService } from "../services/tokenService";
import { getApiUrl } from "../config/api";
import { BuildingOfficeIcon } from "@heroicons/react/24/outline";

export const Settings: React.FC = () => {
  const { user, updateUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");

  // Profile fields
  const [companyName, setCompanyName] = useState(user?.companyName || "");
  const [globalPromptNote, setGlobalPromptNote] = useState(
    (user as any)?.globalPromptNote || ""
  );

  useEffect(() => {
    if (user) {
      setCompanyName(user.companyName || "");
      setGlobalPromptNote((user as any).globalPromptNote || "");
    }
  }, [user]);

  const handleSaveSettings = async () => {
    setIsLoading(true);
    setMessage("");

    try {
      const response = await tokenService.makeAuthenticatedRequest(
        getApiUrl("/api/users/profile"),
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ companyName, globalPromptNote }),
        }
      );

      if (response.ok) {
        updateUser({ companyName, globalPromptNote });
        setMessage("Настройки успешно сохранены");
      } else {
        setMessage("Ошибка при сохранении настроек");
      }
    } catch (error) {
      setMessage("Ошибка при сохранении настроек");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Настройки</h2>

        {/* Профиль пользователя */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <BuildingOfficeIcon className="h-6 w-6 text-gray-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">
              Профиль пользователя
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <input
                type="email"
                value={user?.email || ""}
                disabled
                autoComplete="email"
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500 cursor-not-allowed"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Название компании
              </label>
              <input
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                autoComplete="organization"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Общий комментарий к промпту для генерации ответов
            </label>
            <textarea
              value={globalPromptNote}
              onChange={(e) => setGlobalPromptNote(e.target.value)}
              placeholder="Например: учитывайте наш тон общения, приоритетные темы, ограничения"
              className="w-full min-h-[120px] p-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm text-gray-800 shadow-sm"
            />
            <p className="mt-1 text-xs text-gray-500">
              Будет применяться ко всем ответам ИИ, если не задан комментарий в
              карточке отзыва.
            </p>
          </div>
        </div>

        {/* Сохранение настроек */}
        <div className="flex items-center justify-between">
          <button
            onClick={handleSaveSettings}
            disabled={isLoading}
            className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? "Сохранение..." : "Сохранить настройки"}
          </button>
          {message && (
            <div
              className={`text-sm ${message.includes("Ошибка") ? "text-red-600" : "text-green-600"}`}
            >
              {message}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
